import { isUnavailableOptionalRead } from '../utils/optional-read.utils';
import { isMfaStepUpChallenge } from '../utils/admin-verification.utils';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthErrorService } from '../services/auth-error.service';
import { AuthRecoveryAction } from '../models/auth-errors.model';
import { ToastService } from '@shared/components/ui/toast/toast.service';

/**
 * Endpoints where 401 errors should show a notification (explicit login/auth failures).
 * For all other 401s, we stay silent and let the auth interceptor handle token refresh.
 * If refresh fails, user is redirected to login page - no need for a confusing toast.
 *
 * IMPORTANT: These must match the actual API endpoints:
 * - /api/v1/users/login - User login endpoint
 * - /api/v1/users/register - User registration endpoint
 *
 * This prevents showing "Invalid email or password" when:
 * - Session/token expires and protected requests fail before refresh
 * - Token refresh fails during app initialization
 * - Session validation fails
 */
const SHOW_401_NOTIFICATION_PATTERNS: string[] = [
  '/api/v1/users/login',     // Actual login failures should show notification
  '/api/v1/users/register',  // Registration failures should show notification
  '/users/login',            // Also match without version prefix
  '/users/register'          // Also match without version prefix
];

/**
 * Check if a 401 error should show a notification.
 * Only explicit login/registration failures should show notifications.
 * All other 401s (expired tokens, failed refresh, etc.) are handled silently
 * by the auth interceptor which will redirect to login if needed.
 */
function shouldShow401Notification(url: string): boolean {
  return SHOW_401_NOTIFICATION_PATTERNS.some(pattern => url.includes(pattern));
}

/**
 * Endpoints where 404 errors are expected (resource not found, not auth errors).
 * These should be silenced to avoid spamming the console with [AuthError] for
 * normal business logic like missing tickers or unknown symbols.
 */
const SILENT_404_PATTERNS: string[] = [
  '/api/v1/tickers/',              // Ticker lookups — missing symbols are expected
  '/api/v1/market-data/',          // Market data queries
  '/api/v1/orderbook/',            // Order book data
  '/api/v1/public/orderbook/',     // Public order-book reads (ADMIN-INTERFACE-8H). INTENTIONAL breadth: silences 404s for ALL symbol-scoped public order-book endpoints (heatmap, imbalances, large-orders, depth, metrics, stats, volume-profile, history, price-impact) — for these a 404 means "no data for this symbol yet" (thin/just-listed market), expected business logic, not a fault. Consistent with the broad /market-data//tickers/ entries above.
  '/api/v1/candles/',              // Candle data
  '/api/v1/anomaly-detection/',    // Anomaly detection — backend routes may not exist yet
  // REMOVED 2026-08-20 (Phase 4): the bare substrings '/statements' and
  // '/documents' silenced 404s for ADMIN-INTERFACE-94, justified by the claim
  // that client-portal was their only caller. FULLGAP-CLIENT-001/002 resolved
  // that surface by deleting the callers rather than landing the tables, so the
  // justification no longer holds and the documented removal trigger ("when the
  // backing tables land") could never fire. Left in place they would blanket
  // any future route containing those substrings — e.g.
  // /api/v1/tenants/{id}/documents — hiding a real 404 regression from both the
  // console and Sentry. Do not re-add unprefixed substrings here; if a surface
  // needs suppression, list its fully-qualified '/api/v1/...' prefix.
];

function isExpected404(status: number, url: string, method: string): boolean {
  if (status !== 404) return false;
  // The detail page owns its not-found state. Do not silence analytics or actions.
  const path = url.replace(/^https?:\/\/[^/]+/, '').split(/[?#]/, 1)[0];
  const backtestDetail = method === 'GET' && /^\/api\/v1\/backtests\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(path);
  return backtestDetail || SILENT_404_PATTERNS.some(pattern => url.includes(pattern));
}

/**
 * ADMIN-INTERFACE-8A: a status-0 (transport-layer) failure fetching a static
 * `/assets/` file — e.g. the runtime-config bootstrap when the SPA was loaded from
 * a stale/legacy origin. These fetches carry their own local catchError recovery,
 * so reporting them to Sentry via handleAndReportError is pure noise (and the
 * status-0 → NETWORK_ERROR parser marks it shouldLog). Let the caller's recovery
 * handle it; do not route through the auth-error reporter.
 */
function isExpectedAssetTransportFailure(status: number, url: string): boolean {
  return status === 0 && url.includes('/assets/');
}

/**
 * URL prefixes that represent operator-initiated destructive actions where a
 * 429 (`RazorError::RateLimited`) toast is useful feedback. All OTHER 429s
 * (proxied asset fetches, market-data polls, third-party CDN throttling) are
 * suppressed — the operator did not click anything, so a toast would be
 * confusing.
 *
 * GAP-030 review H-2: the interceptor previously fired on every 429 regardless
 * of URL. Scope restricted to /admin/* and /trading/* (with and without api
 * version prefix).
 */
const RATE_LIMIT_TOAST_URL_PATTERNS: readonly string[] = [
  '/api/v1/admin/',
  '/api/v1/trading/',
  '/api/v1/trading-control/',
  '/api/v1/risk/kill-switch',
  '/api/v1/risk/emergency-actions',
  '/admin/',      // bare (no api prefix)
  '/trading/',    // bare (no api prefix)
];

function shouldShow429Toast(url: string): boolean {
  return RATE_LIMIT_TOAST_URL_PATTERNS.some(pattern => url.includes(pattern));
}

/**
 * Client-side debounce for 429 toasts. Keys the last-emitted timestamp by
 * `${method} ${url}` and suppresses further toasts on the same key within the
 * debounce window. Protects against toast spam when an operator repeatedly
 * clicks a throttled button.
 *
 * GAP-030 review H-1: the original `x-rate-limit-surfaced` anti-loop guard
 * was a no-op because the Rust `RazorError::RateLimited` response does not
 * set that header. This replaces the header check with a deterministic
 * in-memory throttle scoped to the browser tab.
 */
const RATE_LIMIT_TOAST_DEBOUNCE_MS = 5_000;
const rateLimitToastLastEmit: Map<string, number> = new Map();

function shouldEmit429Toast(method: string, url: string): boolean {
  const key = `${method} ${url}`;
  const now = Date.now();
  const last = rateLimitToastLastEmit.get(key);
  if (last !== undefined && now - last < RATE_LIMIT_TOAST_DEBOUNCE_MS) {
    return false;
  }
  rateLimitToastLastEmit.set(key, now);
  // Bound the map so pathological scan traffic can't grow it forever.
  if (rateLimitToastLastEmit.size > 64) {
    const cutoff = now - RATE_LIMIT_TOAST_DEBOUNCE_MS;
    for (const [k, t] of rateLimitToastLastEmit.entries()) {
      if (t < cutoff) { rateLimitToastLastEmit.delete(k); }
    }
  }
  return true;
}

/**
 * DEV-267: Enhanced error interceptor with structured error handling
 * Uses centralized AuthErrorService for consistent error classification
 *
 * CRITICAL: This interceptor must be placed BEFORE authInterceptor in the
 * interceptor array (see app.config.ts). This ensures authInterceptor processes
 * 401 responses FIRST (due to reverse response order) and can attempt token
 * refresh before this interceptor shows any error notification.
 *
 * Silences notifications for:
 * - 401 errors EXCEPT explicit login/registration failures
 *   (all other 401s are handled by authInterceptor with token refresh)
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const authErrorService = inject(AuthErrorService);
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // MFA owns this explicit challenge and its cancellation state. A generic
      // permission/error toast misrepresents it and repeats on background polls.
      if (isMfaStepUpChallenge(error)) return throwError(() => error);
      // GAP-030: surface HTTP 429 (RazorError::RateLimited) to the operator.
      // Rate-limit responses are self-inflicted (operator clicked too fast on
      // a destructive action like kill-switch halt); showing the Retry-After
      // window — if the backend sent one — lets them know how long to wait.
      //
      // H-2: Scoped to operator-initiated admin/trading URLs only so benign
      //      market-data / tickers / asset-proxy 429s don't trigger a
      //      confusing "Server throttled this action" toast.
      // H-1: Replaced the `x-rate-limit-surfaced` header guard (backend does
      //      not set it) with a deterministic client-side debounce keyed on
      //      `${method} ${url}` with a 5s window; this prevents toast spam on
      //      repeated clicks without depending on a backend contract that
      //      currently doesn't exist.
      if (
        error.status === 429 &&
        shouldShow429Toast(req.url) &&
        shouldEmit429Toast(req.method, req.url)
      ) {
        const retryAfter = error.headers?.get('Retry-After');
        const waitHint = retryAfter
          ? ` Retry after ${retryAfter}s.`
          : ' Please wait a moment before retrying.';
        const backendMessage =
          typeof error.error === 'object' && error.error !== null && 'message' in error.error
            ? String((error.error as { message: unknown }).message)
            : undefined;
        const detail = backendMessage && backendMessage.length <= 160
          ? ` ${backendMessage}`
          : '';
        toastService.warning({
          title: 'Rate limit hit',
          message: `Server throttled this action.${waitHint}${detail}`,
          duration: 8000,
        });
      }

      // Determine if notification should be silenced
      let shouldSilent = false;

      // For 401 errors, only show notification for explicit login/registration failures.
      // All other 401s (expired tokens, protected endpoint access, failed refresh, etc.)
      // are handled silently - the auth interceptor will try to refresh and redirect
      // to login if needed. Showing "Invalid credentials" for these would be confusing.
      //
      // NOTE: Due to interceptor ordering in app.config.ts, authInterceptor processes
      // 401s FIRST and will retry with token refresh. If we reach this point with a 401,
      // it means either:
      // 1. This is a login/register request where 401 means bad credentials
      // 2. Token refresh failed (user will be redirected to login)
      if (error.status === 401 && !shouldShow401Notification(req.url)) {
        shouldSilent = true;
      }

      // Skip auth error processing entirely for expected 404s from data endpoints.
      // Missing tickers/symbols are normal business logic, not auth errors.
      // Routing these through AuthErrorService produces misleading [AuthError] logs.
      if (isExpected404(error.status, req.url, req.method) || isUnavailableOptionalRead(error.status, req.url, req.method)) {
        return throwError(() => error);
      }

      // ADMIN-INTERFACE-8A: transport-layer (status 0) failure on a static /assets/
      // fetch — the caller (e.g. runtime-config loader) recovers locally, so don't
      // report it to Sentry. Mirrors the isExpected404 short-circuit above.
      if (isExpectedAssetTransportFailure(error.status, req.url)) {
        return throwError(() => error);
      }

      // Parse error using centralized service (and log/notify)
      const authError = authErrorService.handleAndReportError(error, !shouldSilent);

      // Handle logout for session-related errors
      // Note: authInterceptor may have already triggered logout for 401s,
      // but this catches other session-related errors (e.g., explicit session invalidation)
      if (authError.recoveryAction === AuthRecoveryAction.LOGOUT) {
        authService.logout();
      }

      // Return enhanced error with structured data
      // This allows downstream handlers to access the parsed error
      const enhancedError = Object.assign(error, {
        authError: authError
      });

      return throwError(() => enhancedError);
    })
  );
};
